import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { recipesApi } from '../../api/recipes.api';
import { getRecipeImageUrl } from '../../utils/images';
import { useAuth } from '../../context/AuthContext';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

const getColumns = (cacheKey) => [
  {
    key: 'image',
    label: 'Image',
    render: (row) => {
      const baseImageUrl = getRecipeImageUrl(row.image);
      const imageUrl = baseImageUrl ? `${baseImageUrl}?t=${cacheKey}` : null;
      return (
        <div className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={row.name}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <span className={`text-gray-400 text-xs ${imageUrl ? 'hidden' : 'flex'} items-center justify-center h-full w-full`}>
            No image
          </span>
        </div>
      );
    }
  },
  { key: 'name', label: 'Name (EN)' },
  { key: 'name_es', label: 'Name (ES)' },
  {
    key: 'ingredients',
    label: 'Ingredients',
    render: (row) => (
      <Link
        to={`/recipes/${row._id}/ingredients`}
        onClick={(e) => e.stopPropagation()}
        className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
      >
        {row.ingredients?.length || 0} Ingredients
      </Link>
    )
  },
  {
    key: 'isAI',
    label: 'AI Generated',
    render: (row) =>
      row.isAI ? (
        <Badge variant="info">AI</Badge>
      ) : (
        <Badge variant="secondary">Manual</Badge>
      )
  },
  {
    key: 'is_main',
    label: 'Main Recipe',
    render: (row) =>
      row.is_main ? (
        <Badge variant="success">Yes</Badge>
      ) : (
        <Badge variant="secondary">No</Badge>
      )
  }
];

export default function RecipesListPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [imageCacheKey] = useState(Date.now());

  const fetchRecipes = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      const response = await recipesApi.getAll({ page, search: searchTerm, limit: 20 });
      setRecipes(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipes(1, search);
  }, [search]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await recipesApi.delete(deleteId);
      setDeleteId(null);
      fetchRecipes(pagination.page, search);
    } catch (error) {
      console.error('Error deleting recipe:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Recipes</h1>
        {isAdmin && (
          <Button onClick={() => navigate('/recipes/new')}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Recipe
          </Button>
        )}
      </div>

      <DataTable
        columns={[
          ...getColumns(imageCacheKey),
          ...(isAdmin ? [{
            key: 'actions',
            label: 'Actions',
            align: 'right',
            render: (row) => (
              <div className="flex gap-2 justify-end">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/recipes/${row._id}/edit`);
                  }}
                  className="p-1 text-gray-500 hover:text-primary-600"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteId(row._id);
                  }}
                  className="p-1 text-gray-500 hover:text-red-600"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            )
          }] : [])
        ]}
        data={recipes}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => fetchRecipes(page, search)}
        onSearch={setSearch}
      />

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Recipe"
        message="Are you sure you want to delete this recipe?"
        confirmText="Delete"
        loading={deleting}
      />
    </DashboardLayout>
  );
}
